import { makeRequest } from "@utils/helpers";
import { Button, Col, DatePicker, Form, Input, Row, Select, Drawer, Tag } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import debounce from 'lodash/debounce';
import "../layout/layouts-styles.css";

/**
 * List filters.
 *
 * Basic mode is a wrapping row of compact controls plus a chip row
 * showing what is currently applied — the old version laid the first
 * four filters out in fixed antd columns that overflowed the toolbar,
 * and gave no indication of an active filter beyond the control itself.
 * Advanced mode still opens a drawer with the full set.
 *
 * The debounce-on-change behaviour and the `selected` mutable ref
 * pattern are kept as-is so page-level filter state keeps working.
 */
/** Same keys, same values — enough to spot our own change coming back. */
const shallowEqual = (a, b) => {
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  for (const key of keys) {
    if ((a || {})[key] !== (b || {})[key]) return false;
  }
  return true;
};

const BaseFilter = (props) => {
  const { filters, onFilter, api } = props;
  const [loader, setLoader] = useState(false);
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);

  /*
   * What the boxes are showing.
   *
   * This used to be a `var` reassigned in the render body, which meant
   * two things went wrong. Any re-render reset it, dropping a filter
   * typed but not yet submitted. And the fields read it through
   * `defaultValue`, i.e. they were uncontrolled — so clearing the
   * filters cleared the page's state and left the text sitting in the
   * box, which is why "Clear All" refreshed the list but the email
   * stayed put until it was deleted by hand.
   *
   * Held as state so the fields can be controlled, with a ref alongside
   * it that the debounced submit reads (it outlives the render it was
   * created in).
   */
  const [draft, setDraft] = useState(props.filterData || {});
  const selected = useRef(draft);
  selected.current = draft;

  /*
   * Adopt filters set from outside — Clear All, a chip removed, a page
   * resetting them — but not the echo of what this component just sent
   * up, which would otherwise land mid-word and undo the keystrokes
   * typed while the request was in flight.
   */
  const lastSubmitted = useRef(null);

  useEffect(() => {
    const incoming = props.filterData || {};
    if (lastSubmitted.current !== null && shallowEqual(incoming, lastSubmitted.current)) return;
    setDraft(incoming);
  }, [props.filterData]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSuccess = (data) => setOptions(data);

  useEffect(() => {
    if (api) {
      makeRequest(setLoader, api, null, onSuccess, null);
    }
    // eslint-disable-next-line
  }, []);

  const submit = (values) => {
    lastSubmitted.current = values;
    onFilter(values);
  };

  const onSubmit = () => submit(selected.current);

  const onReset = () => {
    // Empty the boxes here and now; the effect above would otherwise be
    // the only thing that could, and it skips our own echo.
    setDraft({});
    submit({});
  };

  const removeOne = (key) => {
    const next = { ...(props.filterData || {}) };
    delete next[key];
    setDraft(next);
    submit(next);
  };

  /*
   * One debounce for the life of the component. It used to be rebuilt on
   * every render; typing alone does not re-render this component, so it
   * still coalesced a run of keystrokes — but a render arriving mid-edit
   * (the select options landing, or the parent redrawing) left a pending
   * timer holding values from a render that no longer applied, and
   * nothing cancelled it when the bar went away, so a request could land
   * against an unmounted page.
   */
  const debouncedSendRequest = useMemo(
    () => debounce(() => submit(selected.current), 300),
    [onFilter] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => () => debouncedSendRequest.cancel(), [debouncedSendRequest]);

  const onChange = (key, value, each) => {
    const next = each && each.type === "text" ? value.target.value : value;
    const updated = { ...selected.current, [key]: next };

    selected.current = updated;
    setDraft(updated);

    if (props.mode === "basic") {
      debouncedSendRequest();
    }
  };

  /*
   * `value`, not `defaultValue`.
   *
   * These were uncontrolled, so clearing the filters only ever cleared
   * the page's state: "Clear All" refreshed the list and dropped the
   * chips while the text sat there in the box, and removing a single
   * chip left its term visible too. The only way to empty a field was
   * to delete the characters by hand. Reading from `props.filterData`
   * means the box shows what is actually applied.
   */
  const FilterField = (each, i) => {
    const current = draft[each.key];
    const common = {
      key: i,
      placeholder: each.placeholder,
      value: current === undefined ? null : current,
    };

    switch (each.type) {
      case 'text':
        return (
          <Input
            { ...common }
            allowClear
            prefix={ <Icon icon="tabler:search" width={ 16 } style={ { color: 'var(--ink-faint)' } } /> }
            onChange={ (val) => onChange(each.key, val, each) }
          />
        );
      case 'date':
        return (
          <DatePicker
            { ...common }
            format="DD/MM/YYYY"
            onChange={ (val) => onChange(each.key, val) }
          />
        );
      case 'select':
        return (
          <Select
            { ...common }
            allowClear
            options={ options[each.data_key] }
            onChange={ (val) => onChange(each.key, val) }
          />
        );
      default:
        return null;
    }
  };

  /** Human-readable label for an applied filter, for the chip row. */
  const chipLabel = (each, value) => {
    if (each.type === 'select') {
      const list = options[each.data_key] || [];
      const match = list.find((option) => option.value === value);
      return `${each.placeholder}: ${match ? match.label : value}`;
    }
    return `${each.placeholder}: ${value}`;
  };

  const applied = Object.entries(props.filterData || {})
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => ({
      key,
      value,
      each: filters.find((filter) => filter.key === key),
    }))
    .filter((entry) => entry.each);

  if (props.mode === "basic") {
    return (
      <div className="filter-bar">
        <div className="filter-fields">
          { filters.slice(0, 4).map((each, index) => FilterField(each, index)) }
        </div>

        { applied.length > 0 &&
          <div className="filter-chips">
            { applied.map((entry) => (
              <Tag
                key={ entry.key }
                className="filter-chip"
                closable
                closeIcon={ <Icon icon="tabler:x" width={ 13 } /> }
                onClose={ (e) => { e.preventDefault(); removeOne(entry.key); } }
              >
                { chipLabel(entry.each, entry.value) }
              </Tag>
            )) }
            <Button type="link" size="small" onClick={ onReset }>Clear all</Button>
          </div> }
      </div>
    );
  }

  return (
    <>
      <Button
        icon={ <Icon icon="tabler:filter" width={ 18 } /> }
        onClick={ () => setOpen(true) }
      >
        More filters
      </Button>

      <Drawer
        title="Filters"
        onClose={ () => setOpen(false) }
        open={ open }
        width={ 380 }
        className="base-drawer"
      >
        <Form layout="vertical">
          <Row gutter={ [16, 0] }>
            { filters.map((each, index) => (
              <Col span={ 24 } key={ index }>
                <Form.Item label={ each.placeholder }>
                  { FilterField(each, index) }
                </Form.Item>
              </Col>
            )) }
          </Row>

          <div className="drawer-footer" style={ { margin: '0 -24px -24px' } }>
            <Button onClick={ onReset }>Clear all</Button>
            <Button type="primary" onClick={ () => { onSubmit(); setOpen(false); } } loading={ loader }>
              Apply filters
            </Button>
          </div>
        </Form>
      </Drawer>
    </>
  );
};

export default BaseFilter;
